import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import { sentryMask } from 'sentryReplayPrivacy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSage300Connection } from 'app/services/erp-connection.service';

interface Sage300ConnectModalProps {
  open: boolean;
  paidolId: string;
  onSuccess: () => void;
  onClose: () => void;
}

// hh2 site identifiers are bare subdomains of hh2.com (letters/numbers/hyphens).
const SITE_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * SEE-1050 — Sage 300 CRE (hh2) credential-entry connect.
 *
 * Collects the customer's hh2 site identifier + site login. The backend
 * validates against hh2 before saving, so failures shown here are hh2's real
 * rejections (invalid credentials, locked account, unreachable site) — the
 * modal stays open so the user can correct and retry.
 */
function Sage300ConnectModal({ open, paidolId, onSuccess, onClose }: Sage300ConnectModalProps) {
  const { t } = useTranslation();
  const [site, setSite] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedSite = site.trim().toLowerCase();
  const siteInvalid = normalizedSite.length > 0 && !SITE_PATTERN.test(normalizedSite);
  // password.trim() gates whitespace-only input but the password itself is
  // submitted untrimmed — passwords may legitimately contain spaces, and hh2's
  // live validation rejects a wrong one before anything is saved.
  const canSubmit =
    !submitting && SITE_PATTERN.test(normalizedSite) && !!username.trim() && !!password.trim();

  const resetAndClose = () => {
    setSite('');
    setUsername('');
    setPassword('');
    setError(null);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await createSage300Connection(paidolId, normalizedSite, username.trim(), password);
      onSuccess();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('erpConnection.sage300ConnectionFailed', 'Failed to connect to hh2')
      );
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      {...sentryMask}
      open={open}
      onClose={() => !submitting && resetAndClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('erpConnection.sage300ConnectTitle', 'Connect Sage 300 (hh2)')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t(
            'erpConnection.sage300ConnectDescription',
            'Enter the hh2 site and login for your Sage 300 company. These were provided when your hh2 integration was set up.'
          )}
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={t('erpConnection.sage300Site', 'hh2 Site')}
            value={site}
            onChange={(e) => setSite(e.target.value)}
            error={siteInvalid}
            helperText={
              siteInvalid
                ? t(
                    'erpConnection.sage300SiteInvalid',
                    'Enter only the site name — letters, numbers, and hyphens'
                  )
                : ' '
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">.hh2.com</InputAdornment>,
            }}
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label={t('erpConnection.sage300Username', 'Username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label={t('erpConnection.sage300Password', 'Password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ autoComplete: 'new-password' }}
            fullWidth
            disabled={submitting}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <ResponsiveDialogActions>
        <Button onClick={resetAndClose} disabled={submitting}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {submitting ? t('loading') : t('erpConnection.sage300Connect', 'Connect')}
        </Button>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
}

export default Sage300ConnectModal;
